import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAppContext } from '../contexts/AppContext';
import { STANDARD_CONFIGS } from '../constants/standards';
import { PfmeaStandard } from '../types';
import {
    Settings,
    Sun,
    Moon,
    User,
    Shield,
    FileText,
    CheckCircle,
    Info,
    Building,
    Globe,
} from 'lucide-react';

const SettingsPage: React.FC = () => {
    const { user, logout } = useAuth();
    const { theme, setTheme } = useTheme();
    const { activeStandard, setActiveStandard, activeProject, projects, setActiveProject } = useAppContext();

    const standards = Object.values(STANDARD_CONFIGS);

    const industries: Record<string, PfmeaStandard[]> = {
        'Automotive': ['AIAG_VDA', 'AIAG_V4', 'IATF_16949'],
        'Medical Devices': ['ISO_14971'],
        'Aerospace & Defense': ['AS9100D', 'MIL_STD_1629A'],
        'Functional Safety': ['IEC_61508'],
        'General Manufacturing': ['ISO_9001'],
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
                    <Settings className="text-primary-500" />
                    Settings
                </h1>
                <p className="text-[var(--text-secondary)]">
                    Configure your PFMEA preferences and application settings
                </p>
            </div>

            {/* Project Selection */}
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <Building size={20} className="text-primary-500" />
                    Active Project
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {projects.map((project) => (
                        <button
                            key={project}
                            onClick={() => setActiveProject(project)}
                            className={`p-4 rounded-xl border text-left transition-all ${activeProject === project
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                                : 'border-[var(--border-color)] hover:border-primary-300'
                                }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-[var(--text-primary)]">{project}</span>
                                {activeProject === project && (
                                    <CheckCircle size={20} className="text-primary-500" />
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Standard Selection */}
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-primary-500" />
                    PFMEA Standard
                </h2>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                    Select the standard that defines your worksheet columns, risk scales, and calculations.
                </p>

                {/* Standards by Industry */}
                <div className="space-y-6">
                    {Object.entries(industries).map(([industry, standardIds]) => (
                        <div key={industry}>
                            <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Globe size={14} />
                                {industry}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {standardIds.map(stdId => {
                                    const std = STANDARD_CONFIGS[stdId];
                                    if (!std) return null;
                                    return (
                                        <button
                                            key={std.id}
                                            onClick={() => setActiveStandard(std.id)}
                                            className={`p-4 rounded-xl border text-left transition-all ${activeStandard === std.id
                                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                                                : 'border-[var(--border-color)] hover:border-primary-300'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium text-[var(--text-primary)]">{std.name}</span>
                                                {activeStandard === std.id && (
                                                    <CheckCircle size={20} className="text-primary-500" />
                                                )}
                                            </div>
                                            <p className="text-xs text-[var(--text-secondary)] mb-2 line-clamp-2">
                                                {std.description}
                                            </p>
                                            <div className="flex flex-wrap gap-1">
                                                {std.complianceRequirements.slice(0, 3).map(req => (
                                                    <span key={req} className="px-2 py-0.5 text-xs rounded-full bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
                                                        {req}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="mt-2 text-xs text-[var(--text-secondary)]">
                                                {std.useActionPriority ? '✓ Action Priority (H/M/L)' : '✓ RPN-based'}
                                                {' · '}
                                                {std.columns.filter(c => c.visible).length} columns
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Theme Settings */}
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    {theme === 'dark' ? <Moon size={20} className="text-primary-500" /> : <Sun size={20} className="text-amber-500" />}
                    Appearance
                </h2>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setTheme('light')}
                        className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${theme === 'light'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                            : 'border-[var(--border-color)] hover:border-primary-300'
                            }`}
                    >
                        <Sun size={24} className="text-amber-500" />
                        <div className="text-left">
                            <p className="font-medium text-[var(--text-primary)]">Light Mode</p>
                            <p className="text-xs text-[var(--text-secondary)]">Clean and bright</p>
                        </div>
                    </button>
                    <button
                        onClick={() => setTheme('dark')}
                        className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${theme === 'dark'
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                            : 'border-[var(--border-color)] hover:border-primary-300'
                            }`}
                    >
                        <Moon size={24} className="text-indigo-500" />
                        <div className="text-left">
                            <p className="font-medium text-[var(--text-primary)]">Dark Mode</p>
                            <p className="text-xs text-[var(--text-secondary)]">Easy on the eyes</p>
                        </div>
                    </button>
                </div>
            </div>

            {/* User Account */}
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <User size={20} className="text-primary-500" />
                    Account
                </h2>
                <div className="flex items-start gap-4">
                    {user?.photoURL && (
                        <img
                            src={user.photoURL}
                            alt="Profile"
                            className="w-16 h-16 rounded-xl"
                        />
                    )}
                    <div className="flex-1">
                        <p className="font-medium text-[var(--text-primary)]">{user?.displayName}</p>
                        <p className="text-sm text-[var(--text-secondary)]">{user?.email}</p>
                        <div className="mt-2 flex items-center gap-2">
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 capitalize">
                                {user?.role}
                            </span>
                            <span className="text-xs text-[var(--text-secondary)]">
                                {user?.permissions?.length} permissions
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={logout}
                        className="px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                        Sign Out
                    </button>
                </div>
            </div>

            {/* Permissions */}
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <Shield size={20} className="text-primary-500" />
                    Your Permissions
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {user?.permissions?.map(perm => (
                        <div key={perm} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--bg-tertiary)]">
                            <CheckCircle size={14} className="text-green-500" />
                            <span className="text-sm text-[var(--text-primary)]">{perm}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* System Info */}
            <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--border-color)] p-6">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                    <Info size={20} className="text-primary-500" />
                    System Information
                </h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                        <p className="text-[var(--text-secondary)]">Application</p>
                        <p className="font-medium text-[var(--text-primary)]">Magnus PFMEA Portal</p>
                    </div>
                    <div>
                        <p className="text-[var(--text-secondary)]">Version</p>
                        <p className="font-medium text-[var(--text-primary)]">1.0.0</p>
                    </div>
                    <div>
                        <p className="text-[var(--text-secondary)]">Backend</p>
                        <p className="font-medium text-[var(--text-primary)]">magnus-backend (Cloud Run)</p>
                    </div>
                    <div>
                        <p className="text-[var(--text-secondary)]">Database</p>
                        <p className="font-medium text-[var(--text-primary)]">Firestore</p>
                    </div>
                    <div>
                        <p className="text-[var(--text-secondary)]">Authentication</p>
                        <p className="font-medium text-[var(--text-primary)]">Firebase (Shared)</p>
                    </div>
                    <div>
                        <p className="text-[var(--text-secondary)]">Standards Supported</p>
                        <p className="font-medium text-[var(--text-primary)]">{standards.length} standards</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
